import React, { useState, useEffect } from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { 
  Loader2, 
  Save,
  ExternalLink,
  Link2,
  CheckCircle2,
  XCircle,
  Users,
  UserPlus,
  TestTube,
  Tag
} from 'lucide-react';
import { api } from '../../lib/api';
import { toast } from 'sonner';

const LoomyCRMSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [newTag, setNewTag] = useState('');
  const [config, setConfig] = useState({
    enabled: false,
    brand_key: '',
    webhook_url: '',
    default_tags: ['talent-partner'],
    sync_leads: true,
    sync_candidates: true
  });

  useEffect(() => {
    fetchSettings();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/loomy-crm');
      if (response.ok) {
        const data = await response.json();
        setConfig({
          enabled: data.enabled || false,
          brand_key: data.brand_key || '',
          webhook_url: data.webhook_url || '',
          default_tags: data.default_tags || ['talent-partner'],
          sync_leads: data.sync_leads !== false,
          sync_candidates: data.sync_candidates !== false
        });
      }
    } catch (error) {
      console.error('Error fetching Loomy settings:', error);
      toast.error('Erreur lors du chargement des paramètres');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await api.put('/admin/loomy-crm', config);
      if (response.ok) {
        const data = await response.json();
        setConfig({
          ...config,
          webhook_url: data.config?.webhook_url || ''
        });
        toast.success('Paramètres Loomy CRM sauvegardés');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const response = await api.post('/admin/loomy-crm/test');
      const result = await response.json();
      setTestResult(result);
      
      if (result.success) {
        toast.success(`Connexion réussie ! Lead créé: ${result.loomy_lead_id}`);
      } else {
        toast.error(`Échec: ${result.error || 'Erreur inconnue'}`);
      }
    } catch (error) {
      setTestResult({ success: false, error: error.message });
      toast.error('Erreur lors du test');
    } finally {
      setTesting(false);
    }
  };

  const addTag = () => {
    if (newTag && !config.default_tags.includes(newTag)) {
      setConfig({
        ...config,
        default_tags: [...config.default_tags, newTag.toLowerCase().replace(/\s+/g, '-')]
      });
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove) => {
    setConfig({
      ...config,
      default_tags: config.default_tags.filter(tag => tag !== tagToRemove)
    });
  };

  if (loading) {
    return (
      <div className="py-8 text-center text-tp-text-light">
        <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />
        Chargement...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <img 
          src="https://www.loomy-crm.com/logo.png" 
          alt="Loomy CRM" 
          className="h-8 w-8 rounded"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
        <div>
          <h2 className="text-2xl font-display font-semibold text-tp-text">Loomy CRM</h2>
          <p className="text-sm text-tp-text-light">
            Synchronisez automatiquement vos leads et candidats vers votre CRM
          </p>
        </div>
      </div>

      {/* Main Settings Card */}
      <Card className="p-6 border-tp-border">
        <div className="space-y-6">
          {/* Enable/Disable Toggle */}
          <div className="flex items-center justify-between p-4 bg-tp-bg rounded-lg">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-full ${config.enabled ? 'bg-green-100' : 'bg-gray-100'}`}>
                {config.enabled ? (
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                ) : (
                  <XCircle className="h-5 w-5 text-gray-400" />
                )}
              </div>
              <div>
                <Label className="text-base font-medium">Activer l'intégration</Label>
                <p className="text-sm text-tp-text-light">
                  {config.enabled 
                    ? 'Les leads et candidats sont envoyés à Loomy CRM' 
                    : 'L\'intégration est désactivée'}
                </p>
              </div>
            </div>
            <Switch
              checked={config.enabled}
              onCheckedChange={(checked) => setConfig({ ...config, enabled: checked })}
              data-testid="loomy-enabled-toggle"
            />
          </div>

          {/* Brand Key */}
          <div className="space-y-2">
            <Label htmlFor="brand-key">Clé API de votre marque</Label>
            <div className="relative">
              <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-tp-text-light" />
              <Input
                id="brand-key"
                value={config.brand_key}
                onChange={(e) => setConfig({ ...config, brand_key: e.target.value })}
                placeholder="brand_xxxxxxxxx"
                className="pl-10 font-mono"
                data-testid="loomy-brand-key"
              />
            </div>
            <p className="text-xs text-tp-text-light">
              Trouvez cette clé dans Loomy CRM → Réglages → Marques Commerciales
            </p>
          </div>

          {/* Webhook URL (read-only) */}
          {config.brand_key && (
            <div className="space-y-2">
              <Label>URL Webhook (auto-générée)</Label>
              <div className="p-3 bg-tp-bg rounded-lg font-mono text-sm text-tp-text-light break-all">
                https://loomy-crm.com/api/webhook/leads/{config.brand_key}
              </div>
            </div>
          )}

          {/* Sync Options */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex items-center justify-between p-4 bg-tp-bg rounded-lg">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-tp-primary" />
                <span className="text-sm font-medium">Sync Leads (clients)</span>
              </div>
              <Switch
                checked={config.sync_leads}
                onCheckedChange={(checked) => setConfig({ ...config, sync_leads: checked })}
              />
            </div>
            
            <div className="flex items-center justify-between p-4 bg-tp-bg rounded-lg">
              <div className="flex items-center gap-2">
                <UserPlus className="h-4 w-4 text-tp-accent" />
                <span className="text-sm font-medium">Sync Candidats</span>
              </div>
              <Switch
                checked={config.sync_candidates}
                onCheckedChange={(checked) => setConfig({ ...config, sync_candidates: checked })}
              />
            </div>
          </div>

          {/* Default Tags */}
          <div className="space-y-3">
            <Label>Tags par défaut</Label>
            <div className="flex flex-wrap gap-2">
              {config.default_tags.map((tag, index) => (
                <Badge 
                  key={`tag-${tag}-${index}`} 
                  variant="secondary"
                  className="flex items-center gap-1 px-3 py-1"
                >
                  <Tag className="h-3 w-3" />
                  {tag}
                  <button 
                    onClick={() => removeTag(tag)}
                    className="ml-1 text-gray-400 hover:text-gray-600"
                  >
                    ×
                  </button>
                </Badge>
              ))}
            </div>
            <div className="flex gap-2">
              <Input
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                placeholder="Ajouter un tag..."
                onKeyPress={(e) => e.key === 'Enter' && addTag()}
                className="max-w-xs"
              />
              <Button variant="outline" onClick={addTag} disabled={!newTag}>
                Ajouter
              </Button>
            </div>
          </div>
        </div>
      </Card>

      {/* Test Connection */}
      {config.brand_key && (
        <Card className="p-6 border-tp-border">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <TestTube className="h-5 w-5 text-tp-accent" />
              <div>
                <h3 className="font-medium">Tester la connexion</h3>
                <p className="text-sm text-tp-text-light">
                  Envoie un lead de test à Loomy CRM
                </p>
              </div>
            </div>
            <Button
              onClick={handleTest}
              disabled={testing || !config.enabled}
              variant="outline"
              className="gap-2"
              data-testid="loomy-test-connection-btn"
            >
              {testing ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <TestTube className="h-4 w-4" />
              )}
              Tester
            </Button>
          </div>
          
          {testResult && (
            <div className={`mt-4 p-4 rounded-lg ${testResult.success ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`} data-testid="loomy-test-result">
              {testResult.success ? (
                <div className="flex items-center gap-2 text-green-700">
                  <CheckCircle2 className="h-5 w-5" />
                  <span>Connexion réussie ! Lead ID: {testResult.loomy_lead_id}</span>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-red-700">
                  <XCircle className="h-5 w-5" />
                  <span>Échec: {testResult.error} {testResult.details && `- ${testResult.details}`}</span>
                </div>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Save Button */}
      <div className="flex justify-end gap-3">
        <a 
          href="https://www.loomy-crm.com/documentation" 
          target="_blank" 
          rel="noopener noreferrer"
        >
          <Button variant="outline" className="gap-2">
            <ExternalLink className="h-4 w-4" />
            Documentation
          </Button>
        </a>
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-tp-primary hover:bg-tp-primary-light"
          data-testid="save-loomy-settings"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Save className="h-4 w-4 mr-2" />
          )}
          Sauvegarder
        </Button>
      </div>
    </div>
  );
};

export default LoomyCRMSettings;
